import React, { FC, Fragment } from 'react';
import {
  Button,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { ReportRequest, ReportRequestStatus } from 'api';

const TIMEOUT = 3600;

interface IProps extends WithStyles<typeof styles> {
  requests: ReportRequest[];
  count: number;
}

const styles = (theme: Theme) =>
  createStyles({
    title: {
      marginBottom: theme.spacing(1),
    },
    row: {
      height: 92,
    },
  });

const ResultsTable: FC<IProps> = ({ classes, requests, count }) => {
  const { t } = useTranslation();

  function renderHeader() {
    return (
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
        spacing={2}
        className={classes.title}>
        <Grid item>
          <Typography variant="subtitle2">{t('reports:pastReports')}</Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">{t('components:xResult', { count })}</Typography>
        </Grid>
      </Grid>
    );
  }

  function renderTable() {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left" style={{ width: '16%' }}>
              <Typography noWrap>{t('reports:generatedOn')}</Typography>
            </TableCell>
            <TableCell />
            <TableCell style={{ width: '24%' }} />
          </TableRow>
        </TableHead>
        <TableBody>{requests.map(renderRow)}</TableBody>
      </Table>
    );
  }

  function renderRow(request: ReportRequest) {
    const { id, start_at, report_start, report_end, report } = request;
    const { name } = report;
    const { row } = classes;

    return (
      <TableRow key={id} className={row}>
        <TableCell align="left">
          <Typography variant="body2">
            {start_at ? moment.unix(start_at).format('l') : '--'}
          </Typography>
          <Typography color="textSecondary">
            {start_at ? moment.unix(start_at).format('h:mm A') : '--'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="textPrimary">
            {name}
          </Typography>
          <Typography color="textSecondary">
            {t('reports:range', {
              startAt: moment.unix(report_start).format('MMM D, YYYY'),
              endAt: moment.unix(report_end).format('MMM D, YYYY'),
            })}
          </Typography>
        </TableCell>
        <TableCell align="right">{renderDownloadButton(request)}</TableCell>
      </TableRow>
    );
  }

  function renderDownloadButton(request: ReportRequest) {
    let errorText = null;
    if (request.request_status === ReportRequestStatus.Generating) {
      errorText = 'reports:generating';
    } else if (request.end_at < Math.round(new Date().getTime() / 1000) - TIMEOUT) {
      errorText = t('reports:expired');
    } else if (!request.report_url) {
      errorText = 'reports:error';
    }

    if (errorText) {
      return (
        <Button fullWidth variant="outlined" disabled>
          {t(errorText)}
        </Button>
      );
    }

    return (
      <Button target="_blank" href={request.report_url} fullWidth variant="outlined">
        {t('reports:download')}
      </Button>
    );
  }

  return (
    <Fragment>
      {renderHeader()}
      <Paper>{renderTable()}</Paper>
    </Fragment>
  );
};

export default withStyles(styles)(ResultsTable);
